import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ElementType,
  forwardRef,
} from "react";
import classNames from "classnames";
import Link, { type LinkProps } from "next/link";

import ButtonSkeleton from "@/components/ButtonSkeleton";
import IconSvg from "@/components/IconSvg";

export enum ButtonVariants {
  primary = "primary",
  secondary = "secondary",
  text = "text",
}

const getElementType = (
  href?: string,
  hasOnClick?: boolean,
  isTypeSubmit?: boolean,
): ElementType => {
  if (href) {
    return Link;
  }
  return isTypeSubmit || hasOnClick ? "button" : "div";
};

const Icon = ({
  iconFilePath,
  iconClassName,
  iconSize,
}: {
  iconFilePath?: string;
  iconClassName?: string;
  iconSize: { height: string; width: string };
}) => {
  if (!iconFilePath) {
    return null;
  }

  return (
    <IconSvg
      className={classNames("mr-xs", iconClassName)}
      {...iconSize}
      filePath={iconFilePath}
    />
  );
};

const Submitting = ({
  isSubmitting,
  submittingText,
}: {
  isSubmitting: boolean;
  submittingText: React.ReactNode;
}) => {
  if (!isSubmitting) {
    return null;
  }

  return (
    <div className="flex items-center justify-center after:content-['...'] after:animate-ellipsis-dot after:w-[0.85em] after:text-left">
      {submittingText}
    </div>
  );
};

export type ButtonProps = (
  | ButtonHTMLAttributes<HTMLButtonElement>
  | (AnchorHTMLAttributes<HTMLAnchorElement> & LinkProps)
  | AnchorHTMLAttributes<HTMLDivElement>
) & {
  /** Path to an svg file to display next to the button text */
  iconFilePath?: string;
  /** Classnames for svg file to display next to the button text */
  iconClassName?: string;
  /**
   * The button theme
   *
   * **Primary** buttons are used to indicate a primary call to action. There should be one per page at most.
   *
   * **Secondary** buttons can be used when there are two 'primary' calls to action in a single section, or occasionally to open navigation menus.
   *
   * **Text** buttons don't look like typical buttons — they're just bold, underlined text.
   */
  variant?: keyof typeof ButtonVariants;
  /**
   * Allow the button to be full width, and not stopped capped at 322px.
   * Only allowed values are `full` or `undefined` (default).
   */
  width?: "full";
  /**
   * Set the submitting state of the button
   */
  isSubmitting?: boolean;
  /**
   *  Text to show when the button is in a submitting state
   */
  submittingText?: React.ReactNode;
  /**
   * Set the dimensions of the icon (defaults to small)
   */
  iconDimensions?: string;
  /**
   * Set the loading state of the button (i.e. when content is being loaded)
   */
  isLoading?: boolean;
};

const Button = forwardRef<
  HTMLAnchorElement | HTMLButtonElement | HTMLDivElement,
  ButtonProps
>(
  (
    {
      children,
      className,
      iconDimensions,
      iconFilePath,
      iconClassName,
      isSubmitting = false,
      submittingText,
      onClick,
      variant = ButtonVariants.primary,
      width,
      isLoading = false,
      ...others
    },
    ref,
  ) => {
    const widthClasses = classNames({
      "max-w-full": width === "full",
      "md:max-w-[322px]": width !== "full",
    });

    const classes = classNames(
      // general layout classes
      "block rounded-full",
      // typography classes
      "text-center font-bold text-[14px] leading-5",
      {
        // Button variant classes
        "bg-none w-auto underline p-0 text-[14px]":
          variant === ButtonVariants.text,
        "bg-transparent leading-[17px] border-[1.5px]":
          variant === ButtonVariants.secondary,
        "text-white bg-green": variant === ButtonVariants.primary,
        "w-full px-xl py-sm ": variant !== ButtonVariants.text,
      },
      { "flex justify-center items-center": !!iconFilePath },
      widthClasses,
      className,
    );

    const iconSize =
      iconDimensions === "large"
        ? { height: "24", width: "24" }
        : { height: "16", width: "16" };

    const loadingOrDisabled =
      isLoading ||
      isSubmitting ||
      (others as ButtonHTMLAttributes<HTMLButtonElement>).disabled;

    const Element = getElementType(
      (others as AnchorHTMLAttributes<HTMLAnchorElement>).href,
      !!onClick,
      (others as ButtonHTMLAttributes<HTMLButtonElement>).type === "submit",
    );

    if (isLoading) {
      return (
        <ButtonSkeleton
          className={widthClasses}
          height="36"
          aria-busy="true"
          aria-live="polite"
          aria-label="loading button..."
          data-testid={"button-skeleton"}
        />
      );
    }

    return (
      <Element
        onClick={loadingOrDisabled ? undefined : onClick}
        ref={ref}
        className={classes}
        disabled={loadingOrDisabled}
        {...(isSubmitting
          ? {
              "aria-busy": "true",
              "aria-label": submittingText,
              "aria-live": "polite",
            }
          : {})}
        {...others}
      >
        <Icon
          iconFilePath={iconFilePath}
          iconClassName={iconClassName}
          iconSize={iconSize}
        />
        {isSubmitting ? (
          <Submitting
            isSubmitting={isSubmitting}
            submittingText={submittingText}
          />
        ) : (
          children
        )}
      </Element>
    );
  },
);

export default Button;
